import a from '@/styles/about.module.css';
import p from '@/styles/press.module.css';

import { useEffect } from 'react';
import {
  IconArrowDown,
  IconArrowRight,
  IconArrowUpRight,
  IconDownload,
  IconMail,
} from '@tabler/icons-react';
import Image from 'next/image';
import Link from 'next/link';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import { useFetchCmsContentByType } from '@/components/modules/Landing/api';
import CopyBlock from '@/components/modules/Press/CopyBlock';
import FilmFacade from '@/components/modules/Press/FilmFacade';
import content from '@/components/modules/Press/pressContent.json';
import { CMS_PAGE_TITLES } from '@/types/entities/cms.entities';

const EMAIL = content.contactEmail;
const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent('Media inquiry')}`;
const KIT = '/press/chatco-media-kit.zip';
const words = (text: string) => `${text.split(/\s+/).filter(Boolean).length} words`;

const logos = [
  { name: 'Logo, color', file: 'chatco-logo-color', surface: 'light' },
  { name: 'Logo, white', file: 'chatco-logo-white', surface: 'dark' },
  { name: 'Logo, black', file: 'chatco-logo-black', surface: 'light' },
  { name: 'Icon, color', file: 'chatco-icon-color', surface: 'light', icon: true },
  { name: 'Icon, white', file: 'chatco-icon-white', surface: 'dark', icon: true },
] as const;

const colors = [
  { name: 'Chat.co Blue', hex: '#004790', rgb: '0 71 144' },
  { name: 'Star Gold', hex: '#D3A641', rgb: '211 166 65' },
];

const product = [
  {
    file: 'chatco-website-widget',
    title: 'On a website',
    caption:
      'A visitor asks an accounting firm what to bring at tax time. The answer cites the firm’s checklist.',
  },
  {
    file: 'chatco-whatsapp',
    title: 'On WhatsApp',
    caption: 'A surf school answers a customer’s question in WhatsApp and attaches its lesson FAQ.',
  },
  {
    file: 'chatco-spanish',
    title: 'In Spanish',
    caption:
      'An art-supply shop answers a shipping question in Spanish, citing its shipping policy.',
  },
  {
    file: 'chatco-internal-dashboard',
    title: 'For employees',
    caption:
      'A new hire asks the company’s internal agent about time off. The answer cites the handbook.',
  },
];

const stills = [
  ['desk-at-night', 'The desk at night: the office is dark, and Chat.co keeps answering'],
  ['father', 'For them'],
  ['pianist', 'For the music'],
  ['forest', 'For the quiet'],
  ['race', 'For the race'],
  ['painter', 'For the canvas'],
  ['surfer', 'For the waves'],
  ['chef', 'For the kitchen'],
  ['stargazer', 'For the stars'],
  ['cyclist', 'For the ride'],
  ['reader', 'For the story'],
] as const;

const usage = [
  'Use the files as supplied. Do not recolor, stretch, rotate, outline, or add effects.',
  'Use the color logo on light backgrounds and the white logo on dark backgrounds.',
  'Keep clear space around the logo at least the height of the star.',
  'The round icon works where space is square, such as an avatar or app tile.',
];

const PressPage = () => {
  const { data: siteSettings } = useFetchCmsContentByType(CMS_PAGE_TITLES.SITESETTING);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  return (
    <div className={a.page}>
      <NextSeo
        title="Press & Media"
        description="Facts, company descriptions, logos, product visuals, and founder information for journalists covering Chat.co. Media inquiries: press@chat.co."
        canonical="https://chat.co/press"
        openGraph={{
          url: 'https://chat.co/press',
          title: 'Press & Media | Chat.co',
          description: 'Facts, descriptions, and brand assets for journalists covering Chat.co.',
          images: [
            {
              url: 'https://chat.co/images/og-press.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co press and media',
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://chat.co' },
          { position: 2, name: 'Press', item: 'https://chat.co/press' },
        ]}
      />
      <a href="#press-content" className={a.skipLink}>
        Skip to content
      </a>
      <Header contentData={siteSettings?.cmsPage?.pageData ?? undefined} />
      <main id="press-content" tabIndex={-1}>
        <section className={a.hero} aria-labelledby="press-title">
          <div className={`${a.container} ${p.heroGrid}`}>
            <div>
              <p className={a.eyebrow}>
                <span /> PRESS & MEDIA
              </p>
              <h1 id="press-title">
                Chat.co,
                <br />
                <em>for the record.</em>
              </h1>
              <p className={a.heroIntro}>
                Facts, descriptions, and brand assets for journalists, editors, and event
                organizers.
              </p>
              <p className={a.heroBody}>
                Everything on this page is free to use in coverage of Chat.co. For interviews,
                comment, or anything you can’t find here, write to us.
              </p>
              <a href="#about-chatco" className={a.textLink}>
                Start with the facts <IconArrowDown size={17} aria-hidden="true" />
              </a>
            </div>
            <aside className={p.contactCard} aria-labelledby="media-inquiries">
              <p id="media-inquiries" className={p.cardLabel}>
                MEDIA INQUIRIES
              </p>
              <a href={MAILTO} className={p.email}>
                {EMAIL}
              </a>
              <p className={p.cardNote}>Interviews · Comment · Background</p>
              <div className={p.cardActions}>
                <a href={MAILTO} className={p.primary}>
                  <IconMail size={18} aria-hidden="true" /> Email the press team
                </a>
                <a href={KIT} download className={p.secondary}>
                  <IconDownload size={18} aria-hidden="true" /> Download media kit
                  <span className={p.fileMeta}>ZIP · 5 MB</span>
                </a>
              </div>
              <p className={p.kitContents}>
                Logos, product visuals, campaign stills, company descriptions, and founder bio and
                headshot.
              </p>
            </aside>
          </div>
        </section>

        <nav className={a.sectionNav} aria-label="On this page">
          <div className={a.container}>
            <a href="#about-chatco">
              <span>01</span> About Chat.co
            </a>
            <a href="#fast-facts">
              <span>02</span> Fast facts
            </a>
            <a href="#brand-assets">
              <span>03</span> Brand assets
            </a>
            <a href="#visuals">
              <span>04</span> Visuals
            </a>
            <a href="#leadership">
              <span>05</span> Leadership
            </a>
          </div>
        </nav>

        <section id="about-chatco" className={a.section} aria-labelledby="about-title">
          <div className={a.container}>
            <div className={a.sectionHeading}>
              <div>
                <p className={a.eyebrow}>ABOUT CHAT.CO</p>
                <h2 id="about-title">
                  In our words.
                  <br />
                  <em>Ready to use.</em>
                </h2>
              </div>
              <p>Three lengths, so you can use what fits. Copy as written or edit for space.</p>
            </div>
            <div className={p.boilerplates}>
              {content.boilerplates.map((b) => (
                <CopyBlock key={b.id} label={b.label} meta={words(b.text)} text={b.text} />
              ))}
            </div>
          </div>
        </section>

        <section
          id="fast-facts"
          className={`${a.section} ${a.focus}`}
          aria-labelledby="facts-title">
          <div className={a.container}>
            <div className={a.sectionHeading}>
              <div>
                <p className={a.eyebrow}>FAST FACTS</p>
                <h2 id="facts-title">
                  The essentials,
                  <br />
                  <em>at a glance.</em>
                </h2>
              </div>
              <p>
                Verified details for your story. If you need something that is not here, ask us.
              </p>
            </div>
            <dl className={p.facts}>
              {content.facts.map(([term, detail]) => (
                <div key={term}>
                  <dt>{term}</dt>
                  <dd>
                    {term === 'Media contact' ? (
                      <a href={MAILTO}>{detail}</a>
                    ) : term === 'Website' ? (
                      <Link href="/">{detail}</Link>
                    ) : (
                      detail
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <p className={p.nameNote}>{content.nameNote}</p>
          </div>
        </section>

        <section id="brand-assets" className={a.section} aria-labelledby="brand-title">
          <div className={a.container}>
            <div className={a.sectionHeading}>
              <div>
                <p className={a.eyebrow}>BRAND ASSETS</p>
                <h2 id="brand-title">
                  The brand,
                  <br />
                  <em>as supplied.</em>
                </h2>
              </div>
              <p>
                Vector SVG for print and layout, and 3000-pixel PNG with a transparent background
                for everything else.
              </p>
            </div>
            <ul className={p.logoGrid}>
              {logos.map((logo) => (
                <li key={logo.file} className={p.logoTile}>
                  <div
                    className={`${p.logoPreview} ${logo.surface === 'dark' ? p.dark : ''} ${
                      'icon' in logo ? p.iconPreview : ''
                    }`}>
                    {/* eslint-disable-next-line @next/next/no-img-element -- the SVG is shown exactly as downloaded */}
                    <img
                      src={`/press/logos/${logo.file}.svg`}
                      alt={`Chat.co ${logo.name.toLowerCase()}`}
                    />
                  </div>
                  <div className={p.logoMeta}>
                    <span>{logo.name}</span>
                    <span className={p.downloads}>
                      <a href={`/press/logos/${logo.file}.svg`} download>
                        SVG
                      </a>
                      <a href={`/press/logos/${logo.file}.png`} download>
                        PNG
                      </a>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className={p.brandDetails}>
              <div>
                <p className={a.smallLabel}>COLORS</p>
                <ul className={p.swatches}>
                  {colors.map((c) => (
                    <li key={c.hex}>
                      <span className={p.chip} style={{ background: c.hex }} aria-hidden="true" />
                      <span>
                        <strong>{c.name}</strong>
                        <span>
                          {c.hex} · RGB {c.rgb}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className={a.smallLabel}>TYPOGRAPHY</p>
                <p className={p.typeSample}>Inter</p>
                <p className={p.typeNote}>Headlines, interface, and body text</p>
                <p className={`${p.typeSample} ${p.serif}`}>Fraunces Italic</p>
                <p className={p.typeNote}>Accents and campaign lines</p>
              </div>
              <div>
                <p className={a.smallLabel}>USING THE LOGO</p>
                <ul className={p.usage}>
                  {usage.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="visuals" className={`${a.section} ${a.focus}`} aria-labelledby="visuals-title">
          <div className={a.container}>
            <div className={a.sectionHeading}>
              <div>
                <p className={a.eyebrow}>VISUALS</p>
                <h2 id="visuals-title">
                  See it.
                  <br />
                  <em>Show it.</em>
                </h2>
              </div>
              <p>Film, product visuals, and stills, each available at full resolution.</p>
            </div>

            <div className={p.filmGrid}>
              <FilmFacade
                videoId="pSYO4JNFdKU"
                title="More time. For what matters. A Chat.co film (60 seconds)"
                poster="/press/film-poster.jpg"
              />
              <div className={p.filmText}>
                <p className={a.smallLabel}>BRAND CAMPAIGN · 2026</p>
                <h3>More time. For what matters.</h3>
                <p>
                  Ten people, ten passions, and an office where the lights have gone out while
                  Chat.co keeps answering. The campaign includes a 60-second film, ten 30-second
                  films, and vertical versions for short-form video.
                </p>
                <Link href="/more-time" className={a.textLink}>
                  Explore the campaign <IconArrowRight size={17} aria-hidden="true" />
                </Link>
                <a
                  href="https://www.youtube.com/playlist?list=PLOrnr89rtC5I"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={a.textLink}>
                  Watch the films <IconArrowUpRight size={17} aria-hidden="true" />
                  <span className="sr-only"> (opens YouTube in a new tab)</span>
                </a>
                <a
                  href="https://www.youtube.com/playlist?list=PLND0TS9GOb5k"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={a.textLink}>
                  Watch the vertical versions <IconArrowUpRight size={17} aria-hidden="true" />
                  <span className="sr-only"> (opens YouTube in a new tab)</span>
                </a>
              </div>
            </div>

            <h3 className={p.subhead}>Product visuals</h3>
            <p className={p.subNote}>
              Illustrative visuals of Chat.co agents. The businesses shown are fictional.
            </p>
            <ul className={p.productGrid}>
              {product.map((item) => (
                <li key={item.file} className={p.visual}>
                  <div className={p.visualImage}>
                    <Image
                      src={`/press/product/${item.file}.jpg`}
                      alt={`${item.title}: ${item.caption}`}
                      fill
                      sizes="(max-width: 760px) 100vw, 50vw"
                    />
                  </div>
                  <div className={p.visualMeta}>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.caption}</span>
                    </div>
                    <a href={`/press/product/${item.file}.jpg`} download className={p.iconLink}>
                      <IconDownload size={17} aria-hidden="true" />
                      <span className="sr-only">Download {item.title.toLowerCase()} visual</span>
                    </a>
                  </div>
                </li>
              ))}
            </ul>

            <h3 className={p.subhead}>Campaign stills</h3>
            <p className={p.subNote}>
              Frames from the films. Full-resolution files are in the media kit.
            </p>
            <ul className={p.stillGrid}>
              {stills.map(([key, label]) => (
                <li key={key} className={`${p.visual} ${key === 'desk-at-night' ? p.feature : ''}`}>
                  <div className={p.visualImage}>
                    <Image
                      src={`/press/campaign/more-time-${key}.jpg`}
                      alt={`Still from the Chat.co campaign: ${label}`}
                      fill
                      sizes={
                        key === 'desk-at-night'
                          ? '(max-width: 760px) 100vw, 1180px'
                          : '(max-width: 760px) 50vw, 20vw'
                      }
                    />
                  </div>
                  <div className={p.visualMeta}>
                    <span>{label}</span>
                    <a
                      href={`/press/campaign/more-time-${key}.jpg`}
                      download
                      className={p.iconLink}>
                      <IconDownload size={16} aria-hidden="true" />
                      <span className="sr-only">Download still: {label}</span>
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          id="leadership"
          className={`${a.section} ${a.founder}`}
          aria-labelledby="leadership-title">
          <div className={a.container}>
            <div className={a.founderGrid}>
              <figure className={a.founderPortrait}>
                <Image
                  src="/images/about/rafael-sosa-arvelo.jpg"
                  alt="Rafael Sosa Arvelo speaking at a lectern"
                  width={639}
                  height={854}
                  sizes="(max-width: 760px) 100vw, 380px"
                />
                <figcaption>
                  <span>RAFAEL SOSA ARVELO</span>Founder, SA Cyber & Chat.co
                </figcaption>
              </figure>
              <div>
                <p className={a.eyebrow}>LEADERSHIP</p>
                <h2 id="leadership-title">
                  Rafael Sosa Arvelo<span className={a.esq}>, Esq.</span>
                </h2>
                <p className={a.founderLead}>Founder, SA Cyber & Chat.co</p>
                <div className={p.bios}>
                  {content.bios.map((b) => (
                    <CopyBlock
                      key={b.id}
                      label={`${b.label} bio`}
                      meta={words(b.text)}
                      text={b.text}
                    />
                  ))}
                </div>
                <div className={p.leaderLinks}>
                  <a href="/images/about/rafael-sosa-arvelo.jpg" download className={a.textLink}>
                    Download headshot <IconDownload size={17} aria-hidden="true" />
                  </a>
                  <Link href="/about-us#our-founder" className={a.textLink}>
                    More on our About page <IconArrowRight size={17} aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={a.contact} aria-labelledby="contact-title">
          <div className={`${a.container} ${a.contactGrid}`}>
            <div>
              <p className={a.eyebrow}>ON DEADLINE?</p>
              <h2 id="contact-title">
                Write to us.
                <br />
                <em>We’ll help.</em>
              </h2>
              <p>
                Interview requests, comment, background, and assets you can’t find here. Customers
                with product questions can reach us through our contact page.
              </p>
            </div>
            <div className={a.contactActions}>
              <a href={MAILTO} className={a.primaryButton}>
                {EMAIL} <IconArrowUpRight size={19} aria-hidden="true" />
              </a>
              <a href={KIT} download className={a.textLink}>
                Download media kit <IconDownload size={17} aria-hidden="true" />
              </a>
              <Link href="/contact-us" className={a.textLink}>
                Contact sales and support <IconArrowRight size={17} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer contentData={siteSettings?.cmsPage?.pageData ?? undefined} />
    </div>
  );
};

export default PressPage;
